import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from './Badge';
import EntityLink from './EntityLink';
import AuditLog from './AuditLog';

export default function PurchaseOrderPanel({ poId, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Details');

  useEffect(() => {
    api.get(`/purchase-orders/${poId}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [poId]);

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel" style={{ width: 520 }}>
        <div className="drawer-header">
          <h3 className="drawer-title">{data?.po_number || 'Loading…'}</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {data && <Badge variant={data.status === 'open' ? 'success' : 'neutral'}>{data.status}</Badge>}
            <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/purchase-orders/${poId}`)}>
              View Full Page ↗
            </button>
            <button className="drawer-close" onClick={onClose}>×</button>
          </div>
        </div>

        {!data && <div className="panel-body"><div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>Loading…</div></div>}

        {data && (
          <div className="panel-body">
            {/* Tabs */}
            <div className="panel-tabs" style={{ marginBottom: 16 }}>
              {['Details', 'AP Invoices', 'AR Invoices'].map(t => (
                <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
                  {t}
                  {t === 'AP Invoices' && data.ap_invoices?.length > 0 && (
                    <span style={{ marginLeft: 4, fontSize: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 5px' }}>
                      {data.ap_invoices.length}
                    </span>
                  )}
                  {t === 'AR Invoices' && data.ar_invoices?.length > 0 && (
                    <span style={{ marginLeft: 4, fontSize: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 5px' }}>
                      {data.ar_invoices.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Details Tab */}
            {tab === 'Details' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
                <DetailRow label="PO Number" value={data.po_number} />
                <DetailRow label="Status" value={<Badge variant={data.status === 'open' ? 'success' : 'neutral'}>{data.status}</Badge>} />
                <DetailRow label="Period" value={data.period_start && data.period_end ? `${data.period_start} – ${data.period_end}` : null} />
                <DetailRow label="Sell Rate" value={data.sell_rate ? `$${data.sell_rate}/hr` : null} />
                <DetailRow label="Buy Rate" value={data.buy_rate ? `$${data.buy_rate}/hr` : null} />
                <DetailRow label="Client" value={data.client_name} />
                <DetailRow label="Contractor" value={
                  <EntityLink type="contractor" id={data.contractor_id}>{data.contractor_name}</EntityLink>
                } />
                <DetailRow label="Vendor" value={data.vendor_name ?
                  <EntityLink type="vendor" id={data.vendor_id}>{data.vendor_name}</EntityLink> : null
                } />
                <DetailRow label="WO Number" value={data.client_wo_number} />
                <AuditLog tableName="purchase_orders" recordId={poId} />
              </div>
            )}

            {/* AP Invoices Tab */}
            {tab === 'AP Invoices' && (
              <div>
                {(!data.ap_invoices || data.ap_invoices.length === 0) && (
                  <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0' }}>No AP invoices linked.</div>
                )}
                {data.ap_invoices?.map(inv => (
                  <div key={inv.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <EntityLink type="ap_invoice" id={inv.id} style={{ fontWeight: 700, fontSize: 13 }}>{inv.invoice_number}</EntityLink>
                      <Badge>{inv.status}</Badge>
                    </div>
                    <div style={{ color: 'var(--muted)', marginTop: 4 }}>
                      {inv.invoice_date} · ${(inv.amount_to_pay || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* AR Invoices Tab */}
            {tab === 'AR Invoices' && (
              <div>
                {(!data.ar_invoices || data.ar_invoices.length === 0) && (
                  <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0' }}>No AR invoices linked.</div>
                )}
                {data.ar_invoices?.map(inv => (
                  <div key={inv.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <EntityLink type="ar_invoice" id={inv.id} style={{ fontWeight: 700, fontSize: 13 }}>{inv.invoice_number}</EntityLink>
                      <Badge>{inv.status}</Badge>
                    </div>
                    <div style={{ color: 'var(--muted)', marginTop: 4 }}>
                      {inv.invoice_date} · ${(inv.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}
